<template>
<div class="chapter">
<div class="inner_page">
<p class="ch-tit" id="ch1"><b>BIRD ON A VINE PINKEEP</b></p>
<p class="au"><i>Stitched by Renée Plains</i></p>
<p class="intro-txt"><i>I really enjoyed making this small pinkeep. The wool berries, leaves, and vines were especially fun to appliqué. I used toothpick-turn appliqué for the bird and wing. I learned this great appliqué method from my late friend, Laurene Sinema. It uses a round toothpick rather than a needle to turn under the seam allowance. Before you start appliquéing, moisten the toothpick by placing it between your lips. That bit of moisture gives the wooden toothpick a little extra roughness, which makes it easier to turn the fabric under as you stitch. Give it a try. I think you will like it!</i></p>
<p class="h2"><span class="line"><b>MATERIALS</b></span></p>
<p class="h3a"><b>Fabrics</b></p>
<p class="noindent"><b>Linen:</b> 1 fat quarter for pinkeep body</p>
<p class="noindent"><b>Cotton:</b> Brown/black checks and brown print for bird and wing appliqué</p>
<p class="noindent"><b>Wool:</b> Assorted greens and red for vine, leaves, and berries appliqué</p>
<p class="h3"><b>Other materials</b></p>
<p class="noindent"><b>Small bead:</b> 4 mm (approx.) for bird’s eye</p>
<p class="noindent"><b>Small button:</b> For scissor tab</p>
<p class="noindent"><b>Polyester fiberfill</b></p>
<p class="noindent"><b>Crushed walnut shells</b> (Available at pet stores)</p>
<p class="noindent"><b>Embroidery floss:</b> Dark brown</p>
<p class="noindent"><b>Cardboard:</b> For interior base</p>
<p class="h2"><span class="line"><b>CUTTING</b></span></p>
<p class="noindent"><i>Make templates from the Bird on a Vine Pinkeep patterns and cut pieces as indicated</i>.</p>
<p class="h3"><b>Linen</b></p>
<p class="bull">•  Cut 1 rectangle 5˝ × 13˝.</p>
<p class="bull">•  Cut 2 circles from circle pattern.</p>
<p class="bull">•  Cut 1 piece from scissor tab pattern.</p>
<p class="h3"><b>Cardboard</b></p>
<p class="bull">•  Cut 1 circle slightly smaller than the circle pattern.</p>
<p class="h3"><b>Cotton and wool</b></p>
<p class="bull">•  Cut pieces from appliqué pattern (as directed in pattern).</p>
<p class="image"><img alt="" src="images/pg13.jpg"/></p>
<p class="fin"><span class="fin-box">Finished pinkeep: Approx. 4˝ × 4½˝</span></p>
<p class="h4">Sewing</p>
<p class="noindent"><span class="sa">1.</span> Make a template from the Bird on a Vine Pinkeep pattern to use as a placement guide. Appliqué the pieces to the linen pinkeep body. I used a primitive stitch on the wool pieces and needle-turn appliqué for the bird and wings. Add the bead for the bird’s eye.</p>
<p class="tip"><img alt="" class="mi" src="images/tip.jpg"/> The small wool berries can be punched quickly with a Fiskars ¼˝ Circle Hand Punch. (My friend, Sue Spargo, shared this great technique with me.) Another easy way to cut small wool berries is to cut a ¼˝ strip from wool with a rotary cutter. From that strip, cut ¼˝ squares and then round the corners by trimming them with scissors.</p>
<p class="noindent"><span class="sa">2.</span> With right sides together, sew the short ends of the 5˝ × 13˝ rectangle together to make a tube. Fig. A</p>
<p class="image"><img alt="" src="images/pg13_1.jpg"/></p>
<p class="fcap"><i>Fig. A</i></p>
<p class="noindent"><span class="sa">3.</span> Using a ¼˝ seam allowance, sew one of the linen circles to the bottom of the tube by hand or machine. It helps to pin these 2 pieces together at equal intervals around the circle in 4 places. Fig. B</p>
<p class="image"><img alt="" src="images/pg13_2.jpg"/></p>
<p class="fcap"><i>Fig. B</i></p>
<p class="noindent"><span class="sa">4.</span> Turn the pinkeep right side out. Place the cardboard circle inside the pinkeep bottom to help stabilize the pinkeep.</p>
<p class="noindent"><span class="sa">5.</span> Sew a running stitch around the pinkeep top ¼˝ from the top raw edge, leaving the ends long enough to tie.</p>
<p class="noindent"><span class="sa">6.</span> Sew a running stitch around the remaining linen circle (pinkeep top) ¼˝ from the raw edge, leaving the ends long enough to tie. Fig. C</p>
<p class="image"><img alt="" src="images/pg14.jpg"/></p>
<p class="fcap"><i>Fig. C</i></p>
<p class="noindent"><span class="sa">7.</span> Fill the pinkeep with crushed walnut shells to approximately 1˝ from the top.</p>
<p class="noindent"><span class="sa">8.</span> Gather the running stitch on both the pinkeep and the top slightly.</p>
<p class="noindent"><span class="sa">9.</span> Fold the scissor tab in half lengthwise and sew with a <span class="fon">⅛</span>˝ seam allowance. Turn it right side out. Fig. D</p>
<p class="image"><img alt="" src="images/pg14_1.jpg"/></p>
<p class="fcap"><i>Fig. D</i></p>
<p class="noindent"><span class="sa">10.</span> Cut a small slit for the buttonhole a scant ¼˝ from the finished end of the tab. Sew a buttonhole stitch around the slit to finish the raw edge. Fig. E</p>
<p class="image"><img alt="" src="images/pg14_2.jpg"/></p>
<p class="fcap"><i>Fig. E</i></p>
<p class="noindent"><span class="sa">11.</span> Fold back the tab end about 1˝. Mark the center of the buttonhole on the tab behind the slit opening for the button placement. Sew the button in place. Fig. F</p>
<p class="image"><img alt="" src="images/pg14_3.jpg"/></p>
<p class="fcap"><i>Fig. F</i></p>
<p class="noindent"><span class="sa">12.</span> Add some polyester fiberfill over the crushed walnut shells, slightly rounding the top.</p>
<p class="noindent"><span class="sa">13.</span> Place the linen top piece over the stuffing at the top of the pinkeep and pin in several places with the raw edges tucked toward the inside.</p>
<p class="noindent"><span class="sa">14.</span> With the button facing up, tuck the scissor tab raw edge between the pinkeep top and side at a pleasing spot.</p>
<p class="noindent"><span class="sa">15.</span> Sew a whipstitch around the top edge, securely stitching the tab in place.</p>
<p class="noindent"><span class="sa">16.</span> Using embroidery floss, sew a featherstitch over the seamline around the top.</p>
<p class="noindent"><span class="sa">17.</span> Add a small pair of scissors and button.</p>
<p class="image"><img alt="" src="images/pg15.jpg"/></p>
<p class="intro-txt1"><i>Stick a few pins and needles in your finished pinkeep, and it’s ready to use!</i></p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// chapter1.vue?4c36dc38